import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Form, Modal, Row, Select, Tooltip } from "antd";
import MaskedInput from "antd-mask-input";
import { RootStateOrAny, useSelector } from "react-redux";
import { PlusCircleFilled } from "@ant-design/icons";
import moment from "moment/moment";
import { useAppDispatch, useAppSelector } from "../../../../index";
import {
  closePhoneVersion,
  createContactPhone,
  getIndividualInfoById,
  getPhoneVersions,
  updatePhoneVersions,
} from "../../../../store/slices/accountsTs";
import { checkIsLoaded, checkIsLoading, useActiveModal } from "../../../../lib/utils";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import {
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils/formatDate";
import ToggleableVersionsTable from "../../../ToggleableVersionsTable/ToggleableVersionsTable";
import ChangeVersionsTable from "../../../common/ChangeVersionsTable";
import { Phone, PhoneType, TableRecord } from "../../../../lib/interfaces";
import usePhoneMask from "../../../../lib/hooks/usePhoneMask";
import LocalStorageService from "../../../../services/LocalStorageService";

export const AccountContractsDetailsPhone = () => {
  const history = useHistory();
  const [phoneId, setPhoneId] = useState(null);
  const individualId = history.location.search.substring(1).split("=")[1];
  const {
    getIndividualByIdData,
    getIndividualByIdLoading,
    getPhoneVersionsLoading,
    getPhoneVersionsData,
  } = useAppSelector((state) => state.accountsTs);
  const phoneTypes: PhoneType[] = useSelector((state: RootStateOrAny) => state.dicts.phoneTypes);

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);
  const dispatch = useAppDispatch();
  const {
    phoneMask,
    getCountryCodeToDisplay,
    getPhoneToDisplay,
    cleanStringNumber,
  } = usePhoneMask();
  const { activeModal, setActiveModal } = useActiveModal("");

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    countryCode: "20%",
    phone: "25%",
    phoneType: "20%",
    period: "20%",
    actions: "15%",
  };

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      form.setFieldsValue(field);
    }, 0);
  };

  useEffect(() => {
    if (checkIsLoaded(getIndividualByIdLoading)) {
      if (getIndividualByIdData?.properties.length > 0) {
        setTableData(
          getIndividualByIdData?.phones.map((item) => {
            return {
              key: `${item.id}${item.fromDate}`,
              id: item.id,
              fromDate: item.fromDate,
              toDate: item.toDate,
              phone: getPhoneToDisplay(item.phone),
              countryCode: getCountryCodeToDisplay(item.countryCode),
              phoneType: phoneTypes.find(({ id }) => String(id) === String(item.phoneTypeId))?.name,
            };
          })
        );
      }
    }
  }, [getIndividualByIdData, phoneTypes]);

  useEffect(() => {
    if (checkIsLoaded(getPhoneVersionsLoading)) {
      setChildTableData(
        getPhoneVersionsData
          .map((item, i) => {
            return {
              id: item.id,
              fromDate: item.fromDate,
              toDate: item.toDate,
              phone: getPhoneToDisplay(item.phone),
              countryCode: getCountryCodeToDisplay(item.countryCode),
              phoneType: phoneTypes.find(({ id }) => String(id) === String(item.phoneTypeId))?.name,
              ...versionsTableUtils.getCommonChildTableProps(item, i, getPhoneVersionsData.length),
            };
          })
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [getPhoneVersionsData]);

  const [columns, setColumns] = useState([
    {
      title: "Код",
      dataIndex: "countryCode",
      key: "countryCode",
      width: columnsWidths.countryCode,
      hidden: false,
    },
    {
      title: "Номер",
      dataIndex: "phone",
      key: "phone",
      width: columnsWidths.phone,
    },
    {
      title: "Тип номера",
      dataIndex: "phoneType",
      key: "phoneType",
      width: columnsWidths.phoneType,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Код",
      dataIndex: "countryCode",
      key: "countryCode",
      width: columnsWidths.countryCode,
      hidden: false,
      render: (countryCode, record) => {
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({ countryCode });
          return (
            <Form.Item
              name="countryCode"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
            >
              <MaskedInput mask={phoneMask.country} name="countryCode" allowClear />
            </Form.Item>
          );
        }
        return countryCode;
      },
    },
    {
      title: "Номер",
      dataIndex: "phone",
      key: "phone",
      width: columnsWidths.phone,
      render: (phone, record) => {
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({ phone });
          return (
            <Form.Item
              name="phone"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
            >
              <MaskedInput mask={phoneMask.phone} name="phone" allowClear />
            </Form.Item>
          );
        }
        return phone;
      },
    },
    {
      title: "Тип номера",
      dataIndex: "phoneType",
      key: "phoneType",
      width: columnsWidths.phoneType,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (v, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          disabledToDate={(current) =>
            current.isBefore(record.fromDate, "day") || current.isAfter(record.toDate, "day")
          }
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        //  const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              // TODO пока захардкожены hasAccess=true
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const renderCreateModal = () => {
    const closeCreateModal = () => {
      setActiveModal("");
      createForm.resetFields();
    };
    const onFinishCreate = (values) => {
      const phoneNumber = values.phoneNumber.replace(/\D/g, "");
      const countryCode = `+${values.countryCode?.replace(/\D/g, "")}`;
      const tenantId = Number(LocalStorageService.getTenantId());
      const body: Phone = {
        fromDate: toHttpFormatStartOfDay(moment()),
        phoneTypeId: values.phoneTypeId,
        countryCode,
        phone: phoneNumber,
        dsc: "",
        tenantId,
      };
      const data = { individualId, body };
      dispatch(createContactPhone(data)).then(({ payload }) => {
        if (payload) {
          dispatch(getIndividualInfoById({ individualId }));
        }
      });
      closeCreateModal();
    };
    return (
      <Modal
        title="Номер телефона"
        okText="Сохранить"
        visible={activeModal === "accountDetailsCreatePhone"}
        onCancel={closeCreateModal}
        footer={[
          <Button key="back" onClick={closeCreateModal}>
            Отмена
          </Button>,
          <Button key="submit" type="primary" onClick={createForm.submit}>
            Сохранить
          </Button>,
        ]}
      >
        <Form
          form={createForm}
          initialValues={{
            exiteId: "",
            date: "",
          }}
          onFinish={onFinishCreate}
          labelCol={{ span: 6, offset: 3 }}
        >
          <Form.Item
            label="Код страны"
            name="countryCode"
            className="form-item-required__hidden-error"
            wrapperCol={{ span: 4 }}
            rules={[{ required: true }]}
          >
            <MaskedInput mask={phoneMask.country} name="phone" allowClear />
          </Form.Item>
          <Form.Item
            label="Номер"
            name="phoneNumber"
            rules={[{ required: true, message: "Поле обязательно для заполнения" }]}
            className="form-item-required__hidden-error"
            wrapperCol={{ span: 8 }}
          >
            <MaskedInput mask={phoneMask.phone} name="phoneNumber" allowClear />
          </Form.Item>
          <Form.Item
            className="form-item-required__hidden-error__mb0"
            initialValue={1}
            name="phoneTypeId"
            label="Тип номера"
            wrapperCol={{ span: 10 }}
          >
            <Select>
              {phoneTypes.map(({ id, name }) => (
                <Select.Option value={id} key={id}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const onFinish = (values) => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);

    if (version.isCreated || version.isEditing) {
      const isForceUpdate = Boolean(changeableRecord.isEditing);
      const fromDate = isForceUpdate
        ? changeableRecord.fromDate
        : toHttpFormatStartOfDay(values.fromDate);

      const phoneVersion =
        getPhoneVersionsData && getPhoneVersionsData?.find((item) => item?.id === phoneId);

      dispatch(
        updatePhoneVersions({
          phoneId: changeableRecord?.id,
          fromDate,
          force: isForceUpdate,
          body: {
            ...phoneVersion,
            phone: cleanStringNumber(values.phone),
            countryCode: cleanStringNumber(values.countryCode),
          },
        })
      ).then(({ payload }) => {
        if (payload) {
          dispatch(getIndividualInfoById({ individualId }));
          dispatch(getPhoneVersions(Number(phoneId)));
        }
      });
    }

    if (version.isClosing) {
      dispatch(
        closePhoneVersion({
          phoneId: changeableRecord?.id,
          fromDate: toHttpFormat(values.toDate),
        })
      ).then(() => {
        dispatch(getIndividualInfoById({ individualId }));
        dispatch(getPhoneVersions(Number(phoneId)));
      });
    }
  };

  return (
    <div className="account-details-table-wrapper">
      {renderCreateModal()}
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Номер телефона</h4>
              <Tooltip title="Добавить номер телефона">
                <Button
                  style={{ color: "#00BB77", marginLeft: "15px", border: "none" }}
                  shape="circle"
                  icon={<PlusCircleFilled />}
                  onClick={() => setActiveModal("accountDetailsCreatePhone")}
                />
              </Tooltip>
            </Row>
          }
          tableName="account-details-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          childTableData={childTableData}
          isToggleableColumns={false}
          isChildTableLoading={checkIsLoading(getPhoneVersionsLoading)}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded) {
              dispatch(getPhoneVersions(record?.id));
              setPhoneId(record?.id);
            }
          }}
          isPageable={false}
        />
      </Form>
    </div>
  );
};
